import React, { useState } from "react";
import {
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  FormGroup,
  Radio,
  RadioGroup,
  TextField,
  Typography,
  useTheme,
} from "@mui/material";
import QuizIcon from "@mui/icons-material/Quiz";
import { QuestionEvent, QuestionSpec } from "../../../types/ai-event";

interface QuestionCardProps {
  event: QuestionEvent;
  onReply: (requestID: string, answers: string[][]) => void;
}

interface QuestionState {
  selected: Set<string>;
  customValue: string;
}

export const QuestionCard: React.FC<QuestionCardProps> = ({ event, onReply }) => {
  const theme = useTheme();

  const [states, setStates] = useState<QuestionState[]>(
    event.questions.map(() => ({ selected: new Set<string>(), customValue: "" }))
  );
  const [answered, setAnswered] = useState(event.answered ?? false);

  const handleRadioChange = (qi: number, label: string) => {
    setStates((prev) => {
      const next = [...prev];
      next[qi] = { ...next[qi], selected: new Set([label]) };
      return next;
    });
  };

  const handleCheckboxChange = (qi: number, label: string, checked: boolean) => {
    setStates((prev) => {
      const next = [...prev];
      const sel = new Set(next[qi].selected);
      if (checked) {
        sel.add(label);
      } else {
        sel.delete(label);
      }
      next[qi] = { ...next[qi], selected: sel };
      return next;
    });
  };

  const handleCustomChange = (qi: number, value: string) => {
    setStates((prev) => {
      const next = [...prev];
      next[qi] = { ...next[qi], customValue: value };
      return next;
    });
  };

  const handleSubmit = () => {
    const answers: string[][] = event.questions.map((q: QuestionSpec, qi: number) => {
      const sel = Array.from(states[qi].selected);
      if (q.custom && states[qi].customValue.trim()) {
        sel.push(states[qi].customValue.trim());
      }
      return sel;
    });
    setAnswered(true);
    onReply(event.requestID, answers);
  };

  return (
    <Box
      data-testid="question-card"
      sx={{
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: 1,
        overflow: "hidden",
        my: 0.5,
        backgroundColor: theme.palette.background.paper,
        flexShrink: 0,
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", px: 1.5, py: 1, gap: 1 }}>
        <QuizIcon sx={{ fontSize: 16, color: theme.palette.primary.main }} />
        <Typography
          variant="caption"
          sx={{ fontWeight: 600, color: theme.palette.primary.main, lineHeight: 1.2 }}
        >
          Question
        </Typography>
      </Box>

      <Box sx={{ px: 1.5, pb: 1.5, display: "flex", flexDirection: "column", gap: 1.5 }}>
        {event.questions.map((q: QuestionSpec, qi: number) => (
          <Box key={qi}>
            {q.header && (
              <Typography
                variant="caption"
                sx={{ display: "block", fontWeight: 600, mb: 0.5, color: theme.palette.text.secondary }}
              >
                {q.header}
              </Typography>
            )}
            <Typography sx={{ fontSize: "0.875rem", mb: 1, color: theme.palette.text.primary }}>
              {q.question}
            </Typography>

            {q.multiple ? (
              <FormGroup>
                {q.options.map((opt) => (
                  <FormControlLabel
                    key={opt.label}
                    label={opt.label}
                    control={
                      <Checkbox
                        checked={states[qi].selected.has(opt.label)}
                        onChange={(e) => handleCheckboxChange(qi, opt.label, e.target.checked)}
                        disabled={answered}
                        size="small"
                      />
                    }
                  />
                ))}
              </FormGroup>
            ) : (
              <RadioGroup
                value={Array.from(states[qi].selected)[0] ?? ""}
                onChange={(e) => handleRadioChange(qi, e.target.value)}
              >
                {q.options.map((opt) => (
                  <FormControlLabel
                    key={opt.label}
                    value={opt.label}
                    label={opt.label}
                    control={<Radio disabled={answered} size="small" />}
                  />
                ))}
              </RadioGroup>
            )}

            {q.custom && (
              <TextField
                size="small"
                fullWidth
                placeholder="Custom answer…"
                value={states[qi].customValue}
                onChange={(e) => handleCustomChange(qi, e.target.value)}
                disabled={answered}
                sx={{ mt: 1 }}
              />
            )}
          </Box>
        ))}

        <Button
          data-testid="question-submit"
          variant="contained"
          size="small"
          disabled={answered}
          onClick={handleSubmit}
          sx={{ alignSelf: "flex-start" }}
        >
          Submit
        </Button>
      </Box>
    </Box>
  );
};
